'use client';

import Image from 'next/image';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { useTranslations } from 'next-intl';

export default function Footer() {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-50px" });
    const t = useTranslations('Footer');

    return (
        <footer ref={ref} className="py-8 sm:py-12 w-full overflow-hidden bg-gray-50 dark:bg-gray-900">
            <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 w-full">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                    {/* Logo and Company Info */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                        transition={{ duration: 0.6, ease: "easeOut" }}
                    >
                        <div className="flex items-center space-x-2 sm:space-x-3 mb-4">
                            <div className="relative">
                                <Image
                                    src="/paradigma.avif"
                                    alt="Paradigma"
                                    width={60}
                                    height={30}
                                    className="object-contain w-12 h-6 sm:w-16 sm:h-8"
                                />
                            </div>
                            <div>
                                <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-gray-100">{t('company_name')}</h3>
                                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">{t('company_subtitle')}</p>
                            </div>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            {t('company_description')}
                        </p>
                    </motion.div>

                    {/* Contact Information */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                        transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
                    >
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 sm:mb-4">{t('contact_title')}</h3>
                        <div className="space-y-1 sm:space-y-2">
                            <p className="text-sm sm:text-base text-blue-600 dark:text-blue-400 font-medium">
                                {t('phone')}
                            </p>
                            <p className="text-sm sm:text-base text-blue-600 dark:text-blue-400 font-medium">
                                {t('email')}
                            </p>
                        </div>
                    </motion.div>

                    {/* Social Media and Copyright */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                        transition={{ duration: 0.6, ease: "easeOut", delay: 0.4 }}
                    >
                        <div className="flex space-x-2 sm:space-x-4 mb-3 sm:mb-4">
                            {/* Instagram */}
                            <a href="https://www.instagram.com/paradigma.digital3.0/?utm_source=qr&igsh=NWhkdnJsNXhmZ3V2#" target="_blank" rel="noopener noreferrer" className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center hover:scale-110 transition-transform">
                                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                                </svg>
                            </a>

                            {/* Facebook */}
                            <a href="https://www.facebook.com/people/Paradigma-Digital/61577370336103/" target="_blank" rel="noopener noreferrer" className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center hover:scale-110 transition-transform">
                                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                                </svg>
                            </a>

                            {/* YouTube */}
                            <a href="https://youtube.com/@ParadigmaDigital3.0?cbrd=1" target="_blank" rel="noopener noreferrer" className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center hover:scale-110 transition-transform">
                                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                                </svg>
                            </a>


                            <a href="https://inipop-ae0rb7dx9vc1rrnk.builder-preview.com/" target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full flex items-center justify-center hover:scale-110 transition-transform overflow-hidden">
                                <Image
                                    src="/inipop.avif"
                                    alt="Inipop"
                                    width={32}
                                    height={32}
                                    className="w-full h-full object-cover rounded-full"
                                />
                            </a>
                            <a href="https://ai-lab-ar01qrr0gvuw6kge.builder-preview.com/" target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full flex items-center justify-center hover:scale-110 transition-transform overflow-hidden">
                                <Image
                                    src="/ailab.avif"
                                    alt="AI Lab"
                                    width={32}
                                    height={32}
                                    className="w-full h-full object-cover rounded-full"
                                />
                            </a>
                        </div>
                        <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                            <p>{t('copyright')}</p>
                            <p className="text-blue-600 dark:text-blue-400 mt-1 sm:mt-2">{t('powered_by')}</p>
                        </div>
                    </motion.div>
                </div>
            </div>
        </footer>
    );
}
